import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import './Home.scss';
import Seo from '../Seo/Seo.js';
import Reveal from '../../ui/Reveal.js';
import { organizationSchema } from '../../../lib/schema.js';
import { founder, initialsFor } from '../../../content/team.js';

/* ---- Data ---- */
const GLANCE = [
  {
    tag: 'Medical direction',
    t: 'Inpatient rehab units, run well',
    d: 'We medically direct and manage inpatient rehabilitation units inside hospitals, with written admission criteria, clinical workflows, and accountable physician leadership.',
  },
  {
    tag: 'Acute care',
    t: 'Consult services that keep rehab moving',
    d: 'Acute care consult coverage that identifies the right rehab candidates early and keeps the path from acute care to rehabilitation moving smoothly.',
  },
  {
    tag: 'Growing',
    t: 'A focused group, expanding deliberately',
    d: 'Multi-unit coverage across Virginia, now expanding into new markets. Small, selective, and physician-owned.',
  },
];

const APPROACH = [
  {
    n: '01',
    t: 'Written admission criteria',
    d: 'Clear, clinical criteria for who belongs on an inpatient rehab unit, so patients get the right level of care from day one.',
  },
  {
    n: '02',
    t: 'Clinical guidelines by diagnosis',
    d: 'Written guidelines for each diagnosis group, developed over years of running real units.',
  },
  {
    n: '03',
    t: 'Documentation that holds up',
    d: 'Documentation standards that tell the truth clearly and completely.',
  },
  {
    n: '04',
    t: 'Accountable medical direction',
    d: 'Quality metrics, liaison management, and physician leadership that answers for how the unit performs.',
  },
  {
    n: '05',
    t: 'Teams that grow with the unit',
    d: 'Advanced practice providers hired, trained, and integrated as the census grows.',
  },
  {
    n: '06',
    t: 'Software built by our physicians',
    d: 'Tools designed inside the practice and used in it every day. Less time at the keyboard, more at the bedside.',
  },
];

const PARTNER_POINTS = [
  {
    t: 'Serious medical directorship',
    d: 'Written admission criteria, liaison management, documentation standards, and quality metrics. Directorship as a working role.',
  },
  {
    t: 'Documentation that supports reimbursement',
    d: 'Charting built around what reimbursement actually requires, which means fewer documentation queries for your facility.',
  },
  {
    t: 'Reliable staffing and retention',
    d: 'We staff our units the way a high-performing private practice would, and our physicians have reasons to stay.',
  },
  {
    t: 'Fast, direct responsiveness',
    d: 'A focused group, where decisions get made by people you can call.',
  },
];

/* ---- Hero ---- */
function Hero() {
  const reduce = useReducedMotion();
  const rise = (delay) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 26 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay },
        };

  return (
    <section className="home-hero u-grain">
      <div className="home-hero__glow" aria-hidden="true" />
      <div className="u-container home-hero__inner">
        <div className="home-hero__copy">
          <motion.span className="u-eyebrow u-eyebrow--on-dark" {...rise(0.05)}>
            Physician-led inpatient rehabilitation
          </motion.span>
          <motion.h1 className="home-hero__title home-hero__title--long" {...rise(0.12)}>
            Rehabilitation care, refined. <em>Clinical excellence, every day.</em>
          </motion.h1>
          <motion.p className="home-hero__lead" {...rise(0.22)}>
            We&rsquo;ve spent years optimizing every aspect of rehab practice, clinical
            and administrative alike, into an approach we tailor to each hospital, each
            physician, and each patient. So the care our teams deliver is excellent
            by design.
          </motion.p>
          <motion.div className="home-hero__cta" {...rise(0.32)}>
            <Link to="/what-we-do" className="btn btn--gold">
              See how we work <span className="btn__arrow" aria-hidden="true">→</span>
            </Link>
            <Link to="/contact?audience=facility" className="btn btn--ghost-light">
              Partner with us
            </Link>
          </motion.div>
        </div>

        <motion.div
          className="home-hero__visual"
          aria-hidden="true"
          initial={reduce ? false : { opacity: 0, scale: 0.96 }}
          animate={reduce ? {} : { opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
        >
          <div className="pulse">
            {/* expanding heartbeat waves */}
            <span className="pulse__wave w1" />
            <span className="pulse__wave w2" />
            {/* orbit rings; nodes ride them and bloom as each wave arrives */}
            <span className="pulse__ring r1">
              <i className="pulse__node n1" />
              <i className="pulse__node n2" />
            </span>
            <span className="pulse__ring r2">
              <i className="pulse__node n3" />
              <i className="pulse__node n4" />
            </span>
            <span className="pulse__ring r3">
              <i className="pulse__node n5" />
            </span>
            {/* the operating system, the beating heart */}
            <span className="pulse__halo" />
            <span className="pulse__core" />
          </div>
          {/* the domains the operating system runs, each glows as the wave passes */}
          <div className="home-hero__chip chip-a">
            <span className="dot" /> Admission criteria
          </div>
          <div className="home-hero__chip chip-b">
            <span className="dot" /> Charge capture
          </div>
          <div className="home-hero__chip chip-c">
            <span className="dot dot--signal" /> Documentation
          </div>
          <div className="home-hero__chip chip-d">
            <span className="dot" /> Directorships
          </div>
          <div className="home-hero__chip chip-e">
            <span className="dot dot--signal" /> Clinical workflows
          </div>
          <div className="home-hero__chip chip-f">
            <span className="dot" /> Credentialing
          </div>
          <div className="home-hero__chip chip-g">
            <span className="dot dot--signal" /> Hospitalist alignment
          </div>
        </motion.div>
      </div>

      <div className="u-container home-hero__strip">
        <span><strong>Physician-owned</strong> and physician-built</span>
        <span aria-hidden="true" className="home-hero__dot">·</span>
        <span>Operating since <strong>2021</strong></span>
        <span aria-hidden="true" className="home-hero__dot">·</span>
        <span><strong>Multi-unit</strong> coverage across Virginia</span>
        <span aria-hidden="true" className="home-hero__dot">·</span>
        <span>Expanding to <strong>new markets</strong></span>
      </div>
    </section>
  );
}

/* ---- What VRP does at a glance ---- */
function Glance() {
  return (
    <section className="u-section home-glance">
      <div className="u-container">
        <Reveal className="home-glance__head">
          <span className="u-eyebrow">What we do</span>
          <h2>Inpatient rehabilitation, done exceptionally well.</h2>
        </Reveal>
        <div className="home-glance__grid">
          {GLANCE.map((g, i) => (
            <Reveal as="article" key={g.t} className="home-card" delay={0.06 * i}>
              <span className="home-card__tag">{g.tag}</span>
              <h3>{g.t}</h3>
              <p>{g.d}</p>
            </Reveal>
          ))}
        </div>
        <Reveal className="home-glance__more">
          <Link to="/what-we-do" className="home-card__link">
            More on what we do <span aria-hidden="true">→</span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---- The approach (ledger) ---- */
function Approach() {
  return (
    <section className="u-section home-sop">
      <div className="u-container home-sop__grid">
        <div className="home-sop__intro">
          <Reveal>
            <span className="u-eyebrow">How we work</span>
            <h2>An approach refined for every aspect of care.</h2>
            <p className="home-sop__lead">
              Optimized over years on real units, then tailored to each hospital,
              each physician, and each patient. Not one rigid way of doing things,
              but one thoughtful starting point for every detail that matters.
            </p>
            <Link to="/what-we-do" className="btn btn--ghost">
              See how we work <span className="btn__arrow" aria-hidden="true">→</span>
            </Link>
          </Reveal>
        </div>
        <ol className="home-sop__list">
          {APPROACH.map((g, i) => (
            <Reveal as="li" key={g.n} delay={0.04 * i} className="home-sop__item">
              <span className="home-sop__num">{g.n}</span>
              <div>
                <h3>{g.t}</h3>
                <p>{g.d}</p>
              </div>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ---- Physician teaser (the recruit door) ---- */
function PhysicianTeaser() {
  return (
    <section className="home-recruit">
      <div className="u-container home-recruit__inner">
        <Reveal className="home-recruit__copy">
          <span className="u-eyebrow">For physicians</span>
          <h2>
            You bring the clinical ownership. <em>We bring the operating system.</em>
          </h2>
          <p>
            The autonomy of private practice, with real infrastructure behind you.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <Link to="/careers" className="btn btn--gold">
            Explore joining VRP <span className="btn__arrow" aria-hidden="true">→</span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---- Hospital partners (dark) ---- */
function HospitalPartners() {
  return (
    <section className="home-tools u-grain">
      <div className="u-container home-tools__grid">
        <div className="home-tools__copy">
          <Reveal>
            <span className="u-eyebrow u-eyebrow--signal">For hospital partners</span>
            <h2>Your rehab unit,<br /><em>run like it matters.</em></h2>
            <p className="home-tools__lead">
              We operate inpatient rehab units the way a high-performing private
              practice would, with the systems and accountability of a managed group.
            </p>
          </Reveal>
          <ul className="home-tools__list">
            {PARTNER_POINTS.map((t, i) => (
              <Reveal as="li" key={t.t} delay={0.05 * i}>
                <h3>{t.t}</h3>
                <p>{t.d}</p>
              </Reveal>
            ))}
          </ul>
        </div>

        <Reveal className="home-tools__panel" delay={0.1}>
          <div className="home-rank" role="figure" aria-label="One of our units rose from fourth to first in its area for annual rehabilitation volume">
            <span className="home-rank__path" aria-hidden="true">
              <em>4th</em>
              <svg viewBox="0 0 120 40" preserveAspectRatio="none" aria-hidden="true">
                <path d="M4 34 C 40 34, 70 8, 116 6" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
              <strong>1st</strong>
            </span>
            <p className="home-rank__label">
              One of our units rose from 4th to 1st in its area for annual
              rehabilitation volume, with strong outcomes.
            </p>
            <Link to="/contact?audience=facility" className="btn btn--gold home-rank__cta">
              Talk to us about your unit <span className="btn__arrow" aria-hidden="true">→</span>
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---- Founder ---- */
function Authority() {
  return (
    <section className="u-section home-quote">
      <div className="u-container u-container--narrow">
        <Reveal className="home-quote__inner">
          <span className="u-eyebrow">Physician-built</span>
          <blockquote>
            “We built the systems we wished someone had handed us: modern tools,
            written workflows, and a real stake in the outcome.”
          </blockquote>
          <div className="home-quote__by">
            <span className="home-quote__avatar" aria-hidden="true">
              {founder.image && !founder.photoPending ? (
                <img src={founder.image} alt="" />
              ) : (
                initialsFor(founder.name)
              )}
            </span>
            <span className="home-quote__id">
              <strong>{founder.name}</strong>
              <em>Founder</em>
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function HomePage() {
  return (
    <div className="home">
      <Seo
        titleOverride="Virginia Rehab Physicians: Clinical Excellence, Every Day"
        description="A physician-led rehabilitation medicine group. We've optimized every aspect of inpatient rehab, clinical and administrative alike, into an approach we tailor to each hospital, each physician, and each patient."
        path="/"
        jsonLd={[organizationSchema()]}
      />
      <Hero />
      <Glance />
      <Approach />
      <HospitalPartners />
      <PhysicianTeaser />
      <Authority />
    </div>
  );
}

export default HomePage;
